<template>
  <div class="input-strength">
    <button-group
      v-show="closestStrength == null"
      class="input-strength__strengthbuttons"
    >
      <button
        v-for="(presetStrength, strengthKey) in strengthPresets"
        :key="strengthKey"
        class="button"
        type="button"
        @click="$emit('update:strength', presetStrength)"
      >
        <span class="input-strength__strengthlabel">
          {{ $t(`relationStrengths.${strengthKey}`) }}
        </span>
      </button>
    </button-group>
    <div
      class="input-strength__setbox"
      :class="{ 'is-hidden': closestStrength == null }"
    >
      <button
        class="button input-strength__chosenstrength"
        type="button"
        @click="$emit('update:strength', 0)"
      >
        <span class="input-strength__strengthlabel">
          {{
            closestStrength ? $t(`relationStrengths.${closestStrength}`) : "T"
          }}
        </span>
      </button>
      <div class="input-strength__maxinfluence mt-1">
        <input-number
          :min="0"
          :value="strength"
          step="any"
          @update:value="$emit('update:strength', $event)"
        />
        <span v-if="unit">{{ unit }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import ButtonGroup from "@core/input/ButtonGroup.vue";
import InputNumber from "@core/input/InputNumber.vue";

interface Props {
  strength?: number;
  strengthPresets?: Record<string, number>;
  unit?: string;
}
const props = withDefaults(defineProps<Props>(), {
  strength: 0,
  strengthPresets: () => ({
    weak: 10,
    medium: 30,
    strong: 50
  }),
  unit: ""
});
defineEmits<{
  (e: "update:strength", value: number): void;
}>();

const closestStrength = computed(() => {
  if (props.strength == 0) return null;
  if (props.strength == null) return null;
  const strengthPresetKeys = Object.keys(props.strengthPresets);
  strengthPresetKeys.sort(
    (a, b) =>
      Math.abs(props.strength - props.strengthPresets[a]) -
      Math.abs(props.strength - props.strengthPresets[b])
  );
  return strengthPresetKeys[0];
});
</script>

<style lang="scss">
.input-strength {
  display: flex;
  gap: 0.5rem;
  position: relative;
  min-height: 37px;

  &__setbox {
    width: 100%;
    display: flex;
    gap: 0.5rem;
    &.is-hidden {
      visibility: hidden;
      pointer-events: none;
    }
  }

  &__strengthbuttons {
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    /*
    z-index: 2;
    transition: transform 500ms ease-out;

    .button {
      z-index: 1;
    }

    &::before,
    &::after {
      content: "";
      display: block;
      position: absolute;
      z-index: 0;
      background-color: var(--color-background-medium);
      pointer-events: none;
      bottom: 0;
      top: 0;
    }
    &::after {
      left: 1rem;
      right: 1rem;
      transition: transform 125ms ease-out;
      transition-delay: 100ms;
      transform-origin: top left;
    }
    &::before {
      left: 0;
      width: 2rem;
      border-top-left-radius: 400rem;
      border-bottom-left-radius: 400rem;
    }*/
  }

  &__chosenstrength {
    width: 7.5rem;
    flex-basis: 7.5rem;
    flex-shrink: 0;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    padding-right: 1em;
  }

  &__strengthlabel {
    display: inline-block;
    transform: translate3d(0, 0, 0);
    pointer-events: none;
    z-index: 3;

    &.-should-animate {
      transition: transform 125ms ease-out;
    }
  }

  &__maxinfluence {
    width: 100%;
    flex-basis: 100%;
    display: grid;
    grid-template-columns: 4rem 1fr;
    align-items: center;
    gap: 0.5rem;
  }

  &__anim {
    &-leave-to {
      &::after {
        transform: scale3d(0, 1, 1);
      }
      .button {
        opacity: 0;
      }
    }
    /* &-enter-to{} &-leave{} */
  }
}
</style>
